import { ComputedReferenceNodeModel } from "@/models/nodes/ComputedReferenceNodeModel";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";

import { getComputedResultNode } from "./utils";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { NodeJson } from "@corca/data/index.js";

/**
 * Re-link result nodes that were copied without their computed wrapper.
 */
export function resolvePastedComputedResults(
  json: NodeJson,
  doc: DocumentModel,
  wrapperRefId: string | undefined,
): NodeJson[] {
  // A result node's id is its owning wrapper's refId. It's only "at home" when it
  // sits inside that wrapper — copied alone, or rewrapped in a fresh paste wrapper,
  // it can't recalculate and must be re-linked.
  if (json.type === "computedResult" && json.id !== wrapperRefId) {
    const originalRefId = json.id;
    const sourceExists =
      !!originalRefId &&
      doc.textNode.wrapperNodes.some((w) => w.computed && w.refId === originalRefId);

    if (sourceExists) {
      return [
        {
          type: "computedReference",
          props: { targetRefId: originalRefId },
          children: json.children,
        } as NodeJson,
      ];
    }

    // Source is gone — inline the cached value as plain content.
    return json.children[0]?.children ?? [];
  }

  const childWrapperRefId = json.type === "wrapper" ? json.props.refId : wrapperRefId;
  const children = json.children.flatMap((child) =>
    resolvePastedComputedResults(child, doc, childWrapperRefId),
  );

  if (json.type === "computedReference" && json.props.label != null) {
    const { label: _label, ...props } = json.props;
    return [{ ...json, props, children }];
  }

  return [{ ...json, children }];
}

/**
 * Reconcile refIds of freshly pasted/duplicated nodes.
 */
export function alignPastedWrapperRefIds(nodes: NodeModel[]): void {
  const allNodes = nodes.flatMap((node) => [node, ...node.descendants]);

  // old refId -> new refId, so references to a re-keyed wrapper can follow.
  const refIdRemap = new Map<string, string>();

  for (const wrapper of allNodes) {
    if (!wrapper.is(WrapperNodeModel)) continue;

    const oldRefId = wrapper.refId;
    const newRefId = getComputedResultNode(wrapper)?.id;

    if (oldRefId && newRefId && oldRefId !== newRefId) {
      refIdRemap.set(oldRefId, newRefId);
    }

    wrapper.updateProps({ refId: newRefId });
  }

  if (refIdRemap.size === 0) return;

  for (const ref of allNodes) {
    if (!ref.is(ComputedReferenceNodeModel)) continue;
    const newRefId = refIdRemap.get(ref.targetRefId);
    if (newRefId) {
      ref.updateProps({ targetRefId: newRefId });
    }
  }
}
